import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Accordion,
  Badge,
  Alert,
  ListGroup,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import {
  collection,
  addDoc,
  getFirestore,
  Timestamp,
  getDocs,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  FaSave,
  FaArrowLeft,
  FaPlus,
  FaTrash,
  FaClipboardList,
  FaUsers,
  FaCalendarAlt,
  FaCheckCircle,
  FaQuestionCircle,
} from "react-icons/fa";
import firebaseApp from "../../firebase/credenciales";
import alertaOk from "../../functions/alertaOk";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

// Plantilla predeterminada de dimensiones
const PLANTILLA_DIMENSIONES = [
  {
    id: 1,
    nombre: "Cumplimiento de Objetivos",
    peso: 10,
    preguntas: [
      { id: 1, texto: "¿Cumple con las metas y resultados esperados dentro de los plazos establecidos?", escala: 5 },
      { id: 2, texto: "¿Muestra compromiso con los indicadores de rendimiento de su área?", escala: 5 },
      { id: 3, texto: "¿Asume responsabilidad por el logro de los objetivos asignados?", escala: 5 },
      { id: 4, texto: "¿Prioriza adecuadamente sus tareas para cumplir con los resultados?", escala: 5 }
    ]
  },
  {
    id: 2,
    nombre: "Calidad del Trabajo",
    peso: 10,
    preguntas: [
      { id: 1, texto: "¿Entrega trabajos con precisión, sin errores y con buen acabado?", escala: 5 },
      { id: 2, texto: "¿Revisa su trabajo antes de entregarlo?", escala: 5 },
      { id: 3, texto: "¿Aplica estándares de calidad definidos por la organización?", escala: 5 },
      { id: 4, texto: "¿Se preocupa por mejorar continuamente la calidad de sus entregas?", escala: 5 }
    ]
  },
  {
    id: 3,
    nombre: "Conocimientos Técnicos",
    peso: 10,
    preguntas: [
      { id: 1, texto: "¿Posee el conocimiento técnico necesario para desempeñar su función?", escala: 5 },
      { id: 2, texto: "¿Aplica correctamente los procedimientos y normas técnicas?", escala: 5 },
      { id: 3, texto: "¿Mantiene actualizados sus conocimientos profesionales?", escala: 5 },
      { id: 4, texto: "¿Demuestra dominio de herramientas, software o equipos propios de su puesto?", escala: 5 }
    ]
  },
  {
    id: 4,
    nombre: "Seguridad y Salud en el Trabajo",
    peso: 10,
    preguntas: [
      { id: 1, texto: "¿Cumple con las normas y procedimientos de seguridad establecidos?", escala: 5 },
      { id: 2, texto: "¿Participa activamente en las capacitaciones de seguridad?", escala: 5 },
      { id: 3, texto: "¿Utiliza correctamente los equipos de protección personal (EPP)?", escala: 5 },
      { id: 4, texto: "¿Reporta condiciones o actos inseguros a tiempo?", escala: 5 }
    ]
  },
  {
    id: 5,
    nombre: "Trabajo en Equipo",
    peso: 10,
    preguntas: [
      { id: 1, texto: "¿Colabora eficazmente con sus compañeros para lograr objetivos comunes?", escala: 5 },
      { id: 2, texto: "¿Escucha y respeta las opiniones de los demás?", escala: 5 },
      { id: 3, texto: "¿Maneja adecuadamente los desacuerdos o conflictos en su equipo?", escala: 5 },
      { id: 4, texto: "¿Comparte información y recursos con los miembros de su equipo?", escala: 5 }
    ]
  },
  {
    id: 6,
    nombre: "Responsabilidad y Disciplina",
    peso: 10,
    preguntas: [
      { id: 1, texto: "¿Cumple con sus horarios y asistencias de manera puntual?", escala: 5 },
      { id: 2, texto: "¿Maneja con responsabilidad los recursos de la empresa?", escala: 5 },
      { id: 3, texto: "¿Sigue los procedimientos y reglamentos internos?", escala: 5 },
      { id: 4, texto: "¿Acepta las consecuencias de sus actos laborales sin evadir responsabilidades?", escala: 5 }
    ]
  },
  {
    id: 7,
    nombre: "Adaptabilidad y Resolución de Problemas",
    peso: 10,
    preguntas: [
      { id: 1, texto: "¿Se adapta fácilmente a los cambios en su entorno de trabajo?", escala: 5 },
      { id: 2, texto: "¿Propone soluciones prácticas ante problemas?", escala: 5 },
      { id: 3, texto: "¿Responde con serenidad y eficacia en situaciones de presión?", escala: 5 },
      { id: 4, texto: "¿Demuestra flexibilidad ante nuevas tareas o roles?", escala: 5 }
    ]
  },
  {
    id: 8,
    nombre: "Liderazgo",
    peso: 10,
    preguntas: [
      { id: 1, texto: "¿Motiva a su equipo a alcanzar metas y mejorar su desempeño?", escala: 5 },
      { id: 2, texto: "¿Distribuye las tareas de manera equitativa y eficiente?", escala: 5 },
      { id: 3, texto: "¿Da retroalimentación constructiva de manera oportuna?", escala: 5 },
      { id: 4, texto: "¿Toma decisiones claras y firmes en beneficio del equipo?", escala: 5 }
    ]
  },
  {
    id: 9,
    nombre: "Orientación al Cliente",
    peso: 10,
    preguntas: [
      { id: 1, texto: "¿Atiende con amabilidad y profesionalismo a los clientes?", escala: 5 },
      { id: 2, texto: "¿Responde con prontitud a requerimientos o consultas?", escala: 5 },
      { id: 3, texto: "¿Busca soluciones para satisfacer las necesidades del cliente?", escala: 5 },
      { id: 4, texto: "¿Maneja quejas o reclamos de forma empática y efectiva?", escala: 5 }
    ]
  },
  {
    id: 10,
    nombre: "Desarrollo Profesional y Compromiso",
    peso: 10,
    preguntas: [
      { id: 1, texto: "¿Participa en actividades de capacitación o aprendizaje continuo?", escala: 5 },
      { id: 2, texto: "¿Busca activamente mejorar sus competencias?", escala: 5 },
      { id: 3, texto: "¿Se muestra comprometido con la visión y los valores de la empresa?", escala: 5 },
      { id: 4, texto: "¿Propone ideas para mejorar los procesos del área o de la organización?", escala: 5 }
    ]
  }
];

const CrearCampanaPage = ({ usuario }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Datos de la campaña
  const [nombreCampana, setNombreCampana] = useState("");
  const [tipoEvaluacion, setTipoEvaluacion] = useState("180");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [instrucciones, setInstrucciones] = useState("");

  // Dimensiones de evaluación
  const [dimensiones, setDimensiones] = useState([
    { id: 1, nombre: "Desempeño Técnico", peso: 25, preguntas: [] },
    { id: 2, nombre: "Trabajo en Equipo", peso: 25, preguntas: [] },
    { id: 3, nombre: "Liderazgo", peso: 25, preguntas: [] },
    { id: 4, nombre: "Comunicación", peso: 25, preguntas: [] },
  ]);

  // Función para cargar plantilla predeterminada
  const cargarPlantilla = () => {
    setDimensiones(JSON.parse(JSON.stringify(PLANTILLA_DIMENSIONES)));
    setSuccess("✅ Plantilla cargada. Puedes modificar, agregar o eliminar dimensiones según necesites.");
    setTimeout(() => setSuccess(""), 5000);
  };

  const agregarDimension = () => {
    const nuevaDimension = {
      id: Date.now(),
      nombre: "",
      peso: 0,
      preguntas: [],
    };
    setDimensiones([...dimensiones, nuevaDimension]);
  };

  const eliminarDimension = (id) => {
    if (dimensiones.length > 1) {
      setDimensiones(dimensiones.filter((d) => d.id !== id));
    } else {
      setError("Debe haber al menos una dimensión de evaluación");
      setTimeout(() => setError(""), 3000);
    }
  };

  const actualizarDimension = (id, campo, valor) => {
    setDimensiones(
      dimensiones.map((d) => (d.id === id ? { ...d, [campo]: valor } : d))
    );
  };

  const agregarPregunta = (dimensionId) => {
    setDimensiones(
      dimensiones.map((d) => {
        if (d.id === dimensionId) {
          return {
            ...d,
            preguntas: [
              ...(d.preguntas || []),
              { id: Date.now(), texto: "", escala: 5 },
            ],
          };
        }
        return d;
      })
    );
  };

  const eliminarPregunta = (dimensionId, preguntaId) => {
    setDimensiones(
      dimensiones.map((d) => {
        if (d.id === dimensionId) {
          return {
            ...d,
            preguntas: d.preguntas.filter((p) => p.id !== preguntaId),
          };
        }
        return d;
      })
    );
  };

  const actualizarPregunta = (dimensionId, preguntaId, campo, valor) => {
    setDimensiones(
      dimensiones.map((d) => {
        if (d.id === dimensionId) {
          return {
            ...d,
            preguntas: d.preguntas.map((p) =>
              p.id === preguntaId ? { ...p, [campo]: valor } : p
            ),
          };
        }
        return d;
      })
    );
  };

  const validarFormulario = () => {
    if (!nombreCampana.trim()) {
      setError("El nombre de la campaña es obligatorio");
      return false;
    }

    if (!fechaInicio || !fechaFin) {
      setError("Las fechas de inicio y fin son obligatorias");
      return false;
    }

    if (new Date(fechaFin) <= new Date(fechaInicio)) {
      setError("La fecha de fin debe ser posterior a la fecha de inicio");
      return false;
    }

    // Validar dimensiones
    if (dimensiones.length === 0) {
      setError("Debe haber al menos una dimensión de evaluación");
      return false;
    }

    const pesoTotal = dimensiones.reduce(
      (sum, d) => sum + parseFloat(d.peso || 0),
      0
    );
    if (Math.abs(pesoTotal - 100) > 0.1) {
      setError("La suma de los pesos de las dimensiones debe ser 100%");
      return false;
    }

    for (let dim of dimensiones) {
      if (!dim.nombre.trim()) {
        setError("Todas las dimensiones deben tener un nombre");
        return false;
      }
      if (!dim.preguntas || dim.preguntas.length === 0) {
        setError(`La dimensión "${dim.nombre}" debe tener al menos una pregunta`);
        return false;
      }
      for (let preg of dim.preguntas) {
        if (!preg.texto.trim()) {
          setError(`Todas las preguntas en "${dim.nombre}" deben tener texto`);
          return false;
        }
      }
    }

    return true;
  };

  const guardarCampana = async () => {
    setError("");
    setSuccess("");

    if (!validarFormulario()) {
      return;
    }

    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "";

      const nuevaCampana = {
        nombre: nombreCampana.trim(),
        tipoEvaluacion: tipoEvaluacion,
        descripcion: descripcion.trim(),
        instrucciones: instrucciones.trim(),
        fechaInicio: Timestamp.fromDate(new Date(fechaInicio)),
        fechaFin: Timestamp.fromDate(new Date(fechaFin)),
        dimensiones: dimensiones.map((d) => ({
          nombre: d.nombre.trim(),
          peso: parseFloat(d.peso),
          preguntas: (d.preguntas || []).map((p) => ({
            texto: p.texto.trim(),
            escala: parseInt(p.escala),
          })),
        })),
        evaluados: [],
        estado: "borrador",
        creadoPor: usuario?.dni || "",
        fechaCreacion: Timestamp.now(),
        ultimaActualizacion: Timestamp.now(),
      };

      const campanasRef = collection(
        db,
        `evaluaciones/${rucEmpresa}/campañas`
      );
      const docRef = await addDoc(campanasRef, nuevaCampana);

      // AUDITORÍA CREACIÓN CAMPAÑA
      try {
        await logBusinessAction("CREACION_CAMPANA_EVALUACION", "GestionEvaluacionDesempeno", {
          id: docRef.id,
          nombre: nombreCampana.trim(),
          tipoEvaluacion: tipoEvaluacion,
          fechaInicio: fechaInicio,
          fechaFin: fechaFin
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de creación de campaña de evaluación", e);
      }

      setSuccess("¡Campaña creada exitosamente!");
      setTimeout(() => {
        navigate(`/evaluaciones/campana/${docRef.id}`);
      }, 1500);
    } catch (error) {
      console.error("Error al crear campaña:", error);
      setError("Error al crear la campaña. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  // Calcular peso total
  const pesoTotal = dimensiones.reduce(
    (sum, d) => sum + parseFloat(d.peso || 0),
    0
  );

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Indicador de carga */}
        {loading && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
            style={{ zIndex: 1080 }}
          >
            <div className="text-center">
              <Spinner
                animation="border"
                variant="light"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="text-white mt-3 mb-0">Creando campaña...</p>
            </div>
          </div>
        )}

        <Card
          className="shadow-lg border-0"
          style={{ borderRadius: "15px", overflow: "hidden" }}
        >
          {/* Header */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderBottom: "3px solid #ff8a80",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaClipboardList className="me-3" />
                Crear Nueva Campaña de Evaluación
              </h2>
              <p className="mb-0 opacity-75">
                Define las dimensiones y preguntas para evaluar el desempeño
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Alertas */}
            {error && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => setError("")}
                className="mb-4"
              >
                <strong>Error:</strong> {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="mb-4">
                <FaCheckCircle className="me-2" />
                {success}
              </Alert>
            )}

            {/* Información general */}
            <Card
              className="border-0 shadow-sm mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <h5 className="mb-0 fw-semibold">
                  <FaClipboardList className="me-2" />
                  Información General
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Nombre de la Campaña *
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: Evaluación Anual 2024"
                        value={nombreCampana}
                        onChange={(e) => setNombreCampana(e.target.value)}
                        style={{ borderRadius: "10px" }}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Tipo de Evaluación *
                      </Form.Label>
                      <Form.Select
                        value={tipoEvaluacion}
                        onChange={(e) => setTipoEvaluacion(e.target.value)}
                        style={{ borderRadius: "10px" }}
                      >
                        <option value="90">90° (Autoevaluación)</option>
                        <option value="180">180° (Superior + Autoevaluación)</option>
                        <option value="360">360° (Superior + Pares + Subordinados + Auto)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        <FaCalendarAlt className="me-1" />
                        Fecha de Inicio *
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        style={{ borderRadius: "10px" }}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        <FaCalendarAlt className="me-1" />
                        Fecha de Fin *
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        style={{ borderRadius: "10px" }}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Descripción
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Describe el propósito de esta evaluación"
                        value={descripcion}
                        onChange={(e) => setDescripcion(e.target.value)}
                        style={{ borderRadius: "10px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Instrucciones para Evaluadores
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Instrucciones que verán los evaluadores al iniciar"
                        value={instrucciones}
                        onChange={(e) => setInstrucciones(e.target.value)}
                        style={{ borderRadius: "10px" }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Dimensiones de evaluación */}
            <Card
              className="border-0 shadow-sm mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0 fw-semibold">
                    <FaQuestionCircle className="me-2" />
                    Dimensiones de Evaluación
                  </h5>
                  <div className="d-flex align-items-center gap-3">
                    <Badge
                      bg={pesoTotal === 100 ? "success" : "warning"}
                      style={{ fontSize: "0.9rem" }}
                    >
                      Peso Total: {pesoTotal.toFixed(1)}%
                    </Badge>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={cargarPlantilla}
                      className="d-flex align-items-center gap-2 text-white"
                      style={{
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        border: "none"
                      }}
                    >
                      <FaClipboardList size="12px" />
                      Cargar Plantilla
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={agregarDimension}
                      className="d-flex align-items-center gap-2"
                    >
                      <FaPlus size="12px" />
                      Agregar Dimensión
                    </Button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {pesoTotal !== 100 && (
                  <Alert variant="warning" className="mb-3">
                    <FaQuestionCircle className="me-2" />
                    El peso total debe sumar 100%. Actualmente es {pesoTotal.toFixed(1)}%
                  </Alert>
                )}

                <Accordion defaultActiveKey="0">
                  {dimensiones.map((dimension, dimIndex) => (
                    <Accordion.Item
                      eventKey={String(dimIndex)}
                      key={dimension.id}
                      className="mb-3"
                      style={{ borderRadius: "10px", overflow: "hidden" }}
                    >
                      <Accordion.Header>
                        <div className="d-flex align-items-center gap-3 w-100">
                          <Badge
                            style={{
                              background:
                                "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                            }}
                          >
                            Dimensión {dimIndex + 1}
                          </Badge>
                          <span className="fw-bold">
                            {dimension.nombre || "Sin nombre"}
                          </span>
                          <Badge bg="info">{dimension.peso}%</Badge>
                          <Badge bg="secondary">
                            {(dimension.preguntas || []).length} preguntas
                          </Badge>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="bg-light">
                        <Row className="g-3 mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold">
                                Nombre de la Dimensión *
                              </Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Ej: Desempeño Técnico"
                                value={dimension.nombre}
                                onChange={(e) =>
                                  actualizarDimension(
                                    dimension.id,
                                    "nombre",
                                    e.target.value
                                  )
                                }
                                style={{ borderRadius: "8px" }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="fw-semibold">
                                Peso (%) *
                              </Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={dimension.peso}
                                onChange={(e) =>
                                  actualizarDimension(
                                    dimension.id,
                                    "peso",
                                    e.target.value
                                  )
                                }
                                style={{ borderRadius: "8px" }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2} className="d-flex align-items-end">
                            {dimensiones.length > 1 && (
                              <Button
                                variant="outline-danger"
                                className="w-100"
                                onClick={() => eliminarDimension(dimension.id)}
                                style={{ borderRadius: "8px" }}
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </Col>
                        </Row>

                        {/* Preguntas */}
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0 fw-semibold">Preguntas</h6>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => agregarPregunta(dimension.id)}
                              style={{
                                borderRadius: "8px",
                                borderColor: "#f093fb",
                                color: "#f093fb",
                              }}
                            >
                              <FaPlus size="12px" className="me-1" />
                              Agregar Pregunta
                            </Button>
                          </div>

                          {(!dimension.preguntas ||
                            dimension.preguntas.length === 0) && (
                            <Alert variant="info" className="py-2">
                              No hay preguntas. Agrega al menos una.
                            </Alert>
                          )}

                          <ListGroup>
                            {(dimension.preguntas || []).map(
                              (pregunta, pregIndex) => (
                                <ListGroup.Item
                                  key={pregunta.id}
                                  className="mb-2"
                                  style={{ borderRadius: "8px" }}
                                >
                                  <Row className="g-2 align-items-center">
                                    <Col md={1} className="text-center">
                                      <Badge
                                        bg="secondary"
                                        style={{ fontSize: "0.8rem" }}
                                      >
                                        {pregIndex + 1}
                                      </Badge>
                                    </Col>
                                    <Col md={8}>
                                      <Form.Control
                                        type="text"
                                        placeholder="Escribe la pregunta aquí"
                                        value={pregunta.texto}
                                        onChange={(e) =>
                                          actualizarPregunta(
                                            dimension.id,
                                            pregunta.id,
                                            "texto",
                                            e.target.value
                                          )
                                        }
                                        style={{ borderRadius: "6px" }}
                                      />
                                    </Col>
                                    <Col md={2}>
                                      <Form.Select
                                        value={pregunta.escala}
                                        onChange={(e) =>
                                          actualizarPregunta(
                                            dimension.id,
                                            pregunta.id,
                                            "escala",
                                            e.target.value
                                          )
                                        }
                                        size="sm"
                                        style={{ borderRadius: "6px" }}
                                      >
                                        <option value="3">Escala 1-3</option>
                                        <option value="5">Escala 1-5</option>
                                        <option value="10">Escala 1-10</option>
                                      </Form.Select>
                                    </Col>
                                    <Col md={1}>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() =>
                                          eliminarPregunta(
                                            dimension.id,
                                            pregunta.id
                                          )
                                        }
                                        style={{ borderRadius: "6px" }}
                                      >
                                        <FaTrash size="12px" />
                                      </Button>
                                    </Col>
                                  </Row>
                                </ListGroup.Item>
                              )
                            )}
                          </ListGroup>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Card.Body>
            </Card>

            {/* Botones de acción */}
            <div className="d-flex gap-3 justify-content-end">
              <Button
                variant="outline-secondary"
                size="lg"
                onClick={() => navigate("/evaluaciones")}
                className="d-flex align-items-center gap-2"
                style={{ borderRadius: "10px" }}
              >
                <FaArrowLeft />
                Cancelar
              </Button>
              <Button
                className="d-flex align-items-center gap-2 text-white"
                size="lg"
                onClick={guardarCampana}
                disabled={loading}
                style={{
                  borderRadius: "10px",
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  border: "none",
                }}
              >
                <FaSave />
                {loading ? "Guardando..." : "Crear Campaña"}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default CrearCampanaPage;
